import { get, sortBy } from 'lodash';
import { httpClient } from '../../../../utils/httpClient';
import { GET_USER_SESSION_CONTEXT } from '../../../../constants/apiRoutes';
import Logger from '../../../../utils/logger';
import {
  ApiError,
  ContentGeneratedResult,
  ContentTypeImage,
  ContextType,
  GetGeneratedContentParams,
  RecommendedImage,
} from '../../../../types/Content';
import {
  ContentGroup,
  ContentVersion,
  ContextItem,
  EmailModule,
  FileUploadContext,
  GetUserSessionPayload,
  RequestParams,
  TranslatedContentDetail,
  UserSessionContext,
  UserSessionContextApiResponse,
} from '../../../../types/userSessionContext';
import { contextStateSegment } from '../../stateSegments/contextStateSegment';
import store from '../..';
import {
  CHANNELS,
  fileProcessStatuses,
  fileUploadStatuses,
  ANALYTICS,
  GENERATE_CLAIMS,
  SUMMARIZE,
} from '../../../../constants/common';
import {
  contentListStateSegment,
  selectedChannel,
} from '../../stateSegments/contentListSegment';
import {
  getGeneratedContent,
  getGeneratedContentFormatted,
  getGeneratedContentPreview,
  GeneratedContentItem,
} from '../../../../services/Content';
import { getParamsUsed } from '../../../../utils/requestParamsUtils';
import { contentParamsSegment } from '../../stateSegments/paramsSegment';
import { sequenceAndStoreClaims } from './getClaimsReferences';
import { recentTranslationsStateSegment } from '../../stateSegments/recentTranslationsStateSegment';
import { currentSelectedLanguageStateSegment } from '../../stateSegments/availableLanguagesStateSegment';
import { GeneratedContent } from '../../../../types/transformedData/MarkettingContentGeneration';
import {
  ContentImagesHashMap,
  contentImagesStateSegment,
  AssetTypeData,
} from '../../stateSegments/contentImagesStateSegment';
import {
  ModuleContent,
  ModuleStatus,
  SelectedModule,
  OptionData,
} from '../../../../types/store/markettingContentGeneration/emailModules';
import { fileUploadSegment } from '../../../globalStore/stateSegments/fileUploadSegment';
import { FileUploadType } from '../../../../types/ExtractionDetails';
import { extractionDetailsSegment } from '../../../globalStore/stateSegments/extractionDetailsSegment';
import {
  activeEmailScreenStateSegment,
  moduleContentsStateSegment,
  previouslySelectedModules,
  selectedModulesStateSegment,
} from '../../stateSegments/emailModulesStateSegment';
import { restoreModuleContents } from '../../helpers/common';
import {
  setContentSessionId,
  setSessionId,
} from '../../../../utils/commonUtils';
import {
  trackSessionRecoveryInitiate,
  trackSessionRecoveryComplete,
  trackSessionRecoveryError,
} from '../../../../services/Analytics';
import { preGenerateCliamsStateSegment } from '../../stateSegments/preGenerateClaimsStateSegment';
import { ContentEmailModulesSuperScriptType } from '../../../../types/SuperScripts';
import { startNewStateSegment } from '../../stateSegments/startNewStateSegment';
import { getGeneratedContentValidations } from '../microProcessActions/getGeneratedContentValidation';
import { sessionRestoreModalSegment } from '../../stateSegments/sessionRestoreModalSegment';
import { processContentRefData } from '../../../../utils/contentRefDataUtils';
import { DSPGeneratedContent } from '../../../../components/molecules/DSPRenderer/DSPRenderer.types';

// Add a flag to prevent multiple simultaneous executions of restoreChannelModules
let isRestoringChannelModules = false;
// let restoreTimeoutId: NodeJS.Timeout | null = null;

export const getUserSessionContext = async (
  payload: GetUserSessionPayload
): Promise<UserSessionContextApiResponse> => {
  const {
    sessionId,
    includes = ['sessionContext', 'requestParams', 'fileUploadContext'],
  } = payload;

  try {
    const response = await httpClient.get<UserSessionContextApiResponse>(
      GET_USER_SESSION_CONTEXT(sessionId, includes)
    );

    return response as UserSessionContextApiResponse;
  } catch (error) {
    const apiError = error as ApiError;
    Logger.error(new Error(apiError.message));

    return {
      success: false,
      errors: [apiError.message],
      message: apiError.message,
    };
  }
};

type FetchVersionContentReturn = {
  contentVersion: ContentVersion;
  response: GeneratedContent | boolean;
};

const restoreImagesForContent = async (
  data: ContentGeneratedResult['data'],
  contentId: string
) => {
  const generatedContent = get(data, 'generatedContent', []);
  generatedContent[0].imagesRecommend?.forEach(imageRecommend => {
    const { optionId, recommendedImages, frames, imagesReferenceFrame } =
      imageRecommend;

    // Collect all recommended images with their associated frameIds
    type ImageWithFrame = RecommendedImage & { frameId?: string };
    let allRecommendedImages: ImageWithFrame[] = [];

    // Format 1: Direct recommendedImages array (without frames)
    if (recommendedImages && recommendedImages.length > 0) {
      allRecommendedImages = recommendedImages.map(img => ({
        ...img,
        frameId: optionId,
      }));
    }
    // Format 2: Images nested within frames array - aggregate all images from all frames
    else if (frames && frames.length > 0) {
      allRecommendedImages = frames.flatMap(
        frame =>
          frame.recommendedImages?.map(img => {
            // API may return frameId in the image object, otherwise use frame.id
            const imgWithFrame = img as ImageWithFrame;
            return {
              ...img,
              frameId: imgWithFrame.frameId || frame.id,
            };
          }) || []
      );
    }

    // Process all collected images
    if (allRecommendedImages && allRecommendedImages.length > 0) {
      // Segregate images based on imageType
      const imageAssets = allRecommendedImages.filter(
        img => img.imageType === 'IMAGE'
      );
      const chartGraphAssets = allRecommendedImages.filter(
        img => img.imageType !== 'IMAGE'
      );

      store.set(contentImagesStateSegment, state => {
        const optionData: Partial<AssetTypeData> = {};

        if (imageAssets.length > 0) {
          optionData.IMAGE = {
            isLoading: false,
            imagesReferenceFrame: imagesReferenceFrame || '',
            errorFetchingImages: false,
            images: imageAssets.map(image => ({
              name: image.imageName,
              altText: image.caption,
              frameId: image.frameId || optionId,
              id: image.imageId,
              caption: image.caption || '',
              translatedCaption: '',
              category: image.category,
              imageType: image.imageType,
              source: image.source,
              imageMetadata: image.imageMetadata,
            })),
          };
        }

        if (chartGraphAssets.length > 0) {
          optionData.CHART_OR_GRAPH = {
            isLoading: false,
            imagesReferenceFrame: imagesReferenceFrame || '',
            errorFetchingImages: false,
            images: chartGraphAssets.map(image => ({
              name: image.imageName,
              altText: image.caption,
              frameId: image.frameId || optionId,
              id: image.imageId,
              caption: image.caption || '',
              translatedCaption: '',
              category: image.category,
              imageType: image.imageType,
              source: image.source,
              imageMetadata: image.imageMetadata,
            })),
          };
        }

        return {
          ...state,
          [contentId]: {
            ...state[contentId],
            [optionId]: {
              ...state[contentId]?.[optionId],
              ...optionData,
            },
          },
        } as ContentImagesHashMap;
      });
    }
  });
};

const fetchVersionContent = async (
  contentVersion: ContentVersion,
  requestParams: RequestParams
): Promise<FetchVersionContentReturn> => {
  const requestPayload: GetGeneratedContentParams = {
    sessionId: requestParams.sessionId,
    contentSessionId: requestParams.contentSessionId,
    pageSize: 100,
    page: 1,
    contentGroupId: requestParams.contentGroupId,
    action: requestParams.action,
    contentId: contentVersion.contentId,
  };

  if (import.meta.env.MODE === 'development') {
    requestPayload.channel = requestParams.channel;
  }
  const response = await getGeneratedContent(requestPayload);
  if (requestParams.action !== GENERATE_CLAIMS) {
    getGeneratedContentValidations({
      contentId: contentVersion.contentId,
      action: requestParams.action,
      sessionId: requestParams.sessionId,
      contentGroupId: requestParams.contentGroupId,
    });
  }
  const { data, success } = response as unknown as ContentGeneratedResult;

  // Process contentRefData for edited content tracking
  processContentRefData(response, contentVersion.contentId);

  restoreImagesForContent(data, contentVersion.contentId);
  if (data && success) {
    const claimSequences = sequenceAndStoreClaims(data);
    const formattedResponse = getGeneratedContentFormatted(
      requestParams.channel,
      data,
      null,
      claimSequences
    );
    if (requestParams.action === SUMMARIZE) {
      store.set(contentImagesStateSegment, prev => {
        const dspImagesObj: Record<
          number,
          { images: Array<ContentTypeImage>; isLoading: boolean }
        > = {};
        const dspData = formattedResponse?.data?.content as DSPGeneratedContent;
        const summaries = get(dspData, 'contentSummary.summaries', []);

        summaries?.forEach((summary, index) => {
          dspImagesObj[index] = { images: [], isLoading: false };

          dspImagesObj[index].images = (summary.images || []).map(image => {
            const contructedImage: ContentTypeImage = {
              altText: image.page,
              id: image.name,
              frameId: image.name,
              name: image.name,
              caption: image.name,
            };

            return contructedImage;
          });
        });

        return {
          ...prev,
          DSP: {
            ...prev.DSP,
            ...dspImagesObj,
          },
        };
      });
    }
    return {
      contentVersion,
      response: formattedResponse,
    };
  }
  return {
    contentVersion,
    response: false,
  };
};

const restoreTranslatedLanguages = (contentVersion: ContentVersion) => {
  const recentTranslatedLanguages = {
    [contentVersion.contentId]: {
      [contentVersion.versionSequence - 1]: [
        contentVersion.language,
        ...contentVersion.translatedContentDetails.map(
          (obj: TranslatedContentDetail) => obj.translatedLanguage
        ),
      ],
    },
  };
  store.set(recentTranslationsStateSegment, prev => ({
    ...prev,
    ...recentTranslatedLanguages,
  }));
};

export const restoreSessionContent = async (
  sessionContext: ContextItem[],
  requestParams: RequestParams
): Promise<void> => {
  const generatedContentContext = sessionContext.find(
    (contextItem: ContextItem) =>
      contextItem.contextType === ContextType.GENERATION_CONTEXT
  );
  const { paramsUsed, retryPromptParams } = getParamsUsed(requestParams, true);
  store.set(selectedChannel, requestParams.channel);
  if (
    generatedContentContext &&
    requestParams.channel !== CHANNELS.EMAIL.toLowerCase() &&
    requestParams.channel.toLowerCase() !== CHANNELS.RTE.toLowerCase()
  ) {
    store.set(contentListStateSegment, prev => ({
      ...prev,
      isGeneratingContent: true,
    }));
    const contextData: ContentGroup = generatedContentContext
      .contextData[0] as ContentGroup;
    const latestContent = contextData.contentVersions.find(
      (contentVersion: ContentVersion) => contentVersion.isLatestContent
    );
    const versionResponsesPromises = contextData.contentVersions.map(
      (contentVersion: ContentVersion) =>
        fetchVersionContent(contentVersion, requestParams)
    );
    const versionResponses = await Promise.all(versionResponsesPromises);
    if (latestContent) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const contentList: any = sortBy(contextData.contentVersions, [
        'versionSequence',
      ]).map((contentVersion: ContentVersion) => {
        restoreTranslatedLanguages(contentVersion);

        const versionResponse = versionResponses.find(
          (obj: FetchVersionContentReturn) =>
            obj.contentVersion.contentId === contentVersion.contentId
        );
        return {
          [contentVersion.language]:
            versionResponse && versionResponse.response
              ? {
                  ...(versionResponse.response as unknown as GeneratedContent)
                    .data,
                }
              : {
                  id: contentVersion.contentId,
                  content: [],
                  score: null,
                  markdownText: '',
                },
          paramsUsed,
          regenerationLimit: 3,
          apiPayloadUsed: requestParams,
        };
      });
      store.set(contentParamsSegment, prev => ({
        ...prev,
        currentSelectedLanguage: latestContent.language,
        currentSelectedContentType: requestParams.channel,
      }));
      store.set(currentSelectedLanguageStateSegment, latestContent.language);
      store.set(contentListStateSegment, prev => ({
        ...prev,
        isGeneratingContent: false,
        contentList,
        retryPromptParams,
      }));
    }
  }
};

export const restoreSelectedModules = (emailModules: EmailModule[]): void => {
  const modulesMap = emailModules.reduce((map, module) => {
    const key = module.moduleName.toLowerCase();

    if (map.has(key)) {
      map.get(key)!.count += 1;
    } else {
      map.set(key, {
        name: module.moduleName,
        moduleKey: key,
        label: module.moduleDisplayName,
        count: 1,
      });
    }

    return map;
  }, new Map<string, SelectedModule>());
  store.set(selectedModulesStateSegment, modulesMap);
  store.set(previouslySelectedModules, modulesMap);
};

const restoreAllModulesPreviewContent = async (
  emailModuleData: EmailModule[],
  requestParams: RequestParams
) => {
  try {
    // Build requestedContentsData by mapping through each module contentVersions
    const requestedContentsData = emailModuleData
      .flatMap(module =>
        module.contentVersions.map(
          version => `${version.contentId}:${version.language}`
        )
      )
      .join(',');

    // Call preview API once for all modules
    const previewResponse = await getGeneratedContentPreview({
      sessionId: requestParams.sessionId,
      action: requestParams.action,
      contentId: '', // Not needed for bulk preview
      contentGroupId: requestParams.contentGroupId,
      contentSessionId: requestParams.contentSessionId,
      channel: requestParams.channel,
      requestedContentsData,
    });

    if (previewResponse.success && previewResponse.data) {
      const { generatedContent } = previewResponse.data;

      if (generatedContent && generatedContent.length > 0) {
        // Create a map of module contentId to module data for easy lookup
        const moduleDataMap = new Map<
          string,
          { module: EmailModule; selectedContext?: { optionId?: string } }
        >();
        emailModuleData.forEach(module => {
          // Create map entries for all content versions, not just latest
          module.contentVersions.forEach(contentVersion => {
            if (contentVersion.contentId) {
              moduleDataMap.set(contentVersion.contentId, {
                module,
                selectedContext: contentVersion.selectedContext,
              });
            }
          });
        });

        // Process each email module content from the response
        generatedContent.forEach((emailModuleContent: GeneratedContentItem) => {
          // Match module's latestContent.contentId with emailModuleContent.id
          const matchingModuleData = moduleDataMap.get(emailModuleContent.id);
          if (!matchingModuleData) return;

          const { module: matchingModule, selectedContext } =
            matchingModuleData;
          const selectedOptionId = selectedContext?.optionId;

          let selectedOption: OptionData | null = null;
          let selectedOptionSuperScripts: ContentEmailModulesSuperScriptType | null =
            null;

          // Only process if selectedContext.optionId is not empty
          if (selectedOptionId) {
            // Map the Swagger format to the expected format - find matching optionId
            if (
              emailModuleContent.contentOptions &&
              emailModuleContent.contentOptions.length > 0
            ) {
              const contentOption = emailModuleContent.contentOptions.find(
                option => option.optionId === selectedOptionId
              );
              if (contentOption) {
                selectedOption = {
                  isRefined: false,
                  attributes: emailModuleContent.attributes || [],
                  content: {
                    optionId: contentOption.optionId,
                    header: contentOption.header || '',
                    preHeader: contentOption.preHeader || '',
                    body: contentOption.body || '',
                    closingBody: contentOption.closingBody || '',
                    callToAction: contentOption.clickToAction || '',
                    subheader: contentOption.subheader || '',
                    bulletedCopy: contentOption.bulletedCopy || [],
                    subjectLine: contentOption.subjectLine || '',
                    previewText: contentOption.previewText || '',
                  },
                };
              }
            }

            // Map superscripts from the Swagger format - find matching optionId
            if (
              emailModuleContent.superScripts &&
              emailModuleContent.superScripts.length > 0
            ) {
              const superScript = emailModuleContent.superScripts.find(
                script => script.optionId === selectedOptionId
              );
              if (superScript) {
                const superScriptRefs = superScript.superScriptReferences || {};
                selectedOptionSuperScripts = {
                  optionId: superScript.optionId,
                  isLoading: false,
                  header:
                    superScriptRefs.header?.map(id => ({
                      label: id,
                      value: id,
                    })) || [],
                  preHeader:
                    superScriptRefs.preHeader?.map(id => ({
                      label: id,
                      value: id,
                    })) || [],
                  body:
                    superScriptRefs.body?.map(id => ({
                      label: id,
                      value: id,
                    })) || [],
                  closingBody:
                    superScriptRefs.closingBody?.map(id => ({
                      label: id,
                      value: id,
                    })) || [],
                  clickToAction:
                    superScriptRefs.clickToAction?.map(id => ({
                      label: id,
                      value: id,
                    })) || [],
                  subheader:
                    superScriptRefs.subheader?.map(id => ({
                      label: id,
                      value: id,
                    })) || [],
                  bulletedCopy:
                    superScriptRefs.bulletedCopy?.map(id => ({
                      label: id,
                      value: id,
                    })) || [],
                  subjectLine:
                    superScriptRefs.subjectLine?.map(id => ({
                      label: id,
                      value: id,
                    })) || [],
                  previewText:
                    superScriptRefs.previewText?.map(id => ({
                      label: id,
                      value: id,
                    })) || [],
                };
              }
            }
          }

          // Update the module content with preview data
          store.set(moduleContentsStateSegment, prevModules => {
            const updatedModules = new Map(prevModules);
            const module = updatedModules.get(matchingModule.mapKey);

            if (module) {
              const updatedModule = {
                ...module,
                selectedOption: selectedOption
                  ? (selectedOption as unknown as OptionData)
                  : null,
                selectedOptionSuperScripts: selectedOptionSuperScripts
                  ? (selectedOptionSuperScripts as unknown as ContentEmailModulesSuperScriptType)
                  : null,
              };
              updatedModules.set(matchingModule.mapKey, updatedModule);
            }

            return updatedModules;
          });
        });
      }
    }
  } catch (error) {
    Logger.error(
      new Error(`Error restoring modules preview content: ${error}`)
    );
  }
};

export const restoreEmailModules = async (
  emailModuleData: EmailModule[],
  isRTE: boolean = false
): Promise<void> => {
  const contextState = store.get(contextStateSegment);
  const requestParams = contextState.requestPayload;

  const modulesMap = emailModuleData.reduce((map, module, index) => {
    const {
      moduleKey,
      mapKey,
      contentVersions,
      moduleName,
      moduleDisplayName,
      contentGroupId,
    } = module;
    const hasContent = contentVersions.length > 0;
    const latestContent = contentVersions.find(
      version => version.isLatestContent
    ) || {
      contentId: '',
      versionSequence: 0,
      operationType: '',
      language: '',
      translatedContentDetails: [],
      isLatestContent: true,
    };
    const context = latestContent.selectedContext;
    const hasOptionSelected = !!context?.optionId;
    const hasImageSelected = !!context?.imageId;
    const selectedOptionLanguage = context?.language || '';
    const parentContentId = hasContent ? latestContent.contentId : '';

    const { contentId } = latestContent;

    contentVersions.forEach((contentVersion: ContentVersion) => {
      restoreTranslatedLanguages(contentVersion);
    });

    // Construct generatedContents object from contentVersions
    const generatedContents = contentVersions.reduce(
      (acc, contentVersion) => {
        // Use versionSequence as the key (convert to string)
        acc[contentVersion.versionSequence.toString()] = {
          contentId: contentVersion.contentId,
        };
        return acc;
      },
      {} as Record<string, { contentId: string }>
    );

    let status: ModuleStatus = 'Not Started';
    if (hasContent) status = 'In Progress';
    if (hasOptionSelected) status = 'Ready';
    const selectedModule: SelectedModule = {
      moduleKey,
      name: moduleName,
      label: moduleDisplayName,
      count: 1,
    };
    const restoredModule = restoreModuleContents(
      selectedModule,
      mapKey,
      isRTE ? moduleDisplayName : '',
      index
    );
    map.set(mapKey, {
      ...restoredModule,
      mapKey,
      moduleKey,
      status,
      hasOptionSelected,
      hasImageSelected,
      selectedOptionLanguage,
      contentId,
      selectedOptionId: context?.optionId || '',
      selectedImageName: context?.imageId || '',
      parentContentId,
      contentGroupId: contentGroupId || restoredModule.contentGroupId, // Preserve existing contentGroupId or use generated one
      generatedContents, // Add the constructed generatedContents object
    });
    return map;
  }, new Map<string, ModuleContent>());

  store.set(moduleContentsStateSegment, modulesMap);
  store.set(activeEmailScreenStateSegment, 'modules-dashboard');

  // Restore preview content for modules that have content
  if (requestParams) {
    const modulesWithContent = emailModuleData.filter(
      module => module.contentVersions.length > 0
    );

    if (modulesWithContent.length > 0) {
      await restoreAllModulesPreviewContent(modulesWithContent, requestParams);
    }
  }
};

export const restoreChannelModules = async (): Promise<void> => {
  if (isRestoringChannelModules) {
    return;
  }

  isRestoringChannelModules = true;

  try {
    // Get data from store instead of parameters
    const contextState = store.get(contextStateSegment);
    const sessionContext = contextState.sessionContext || [];
    const requestParams = contextState.requestPayload;
    const selectedEmailModules = contextState.selectedEmailModules || [];

    if (!requestParams) {
      Logger.error(new Error('No request params found in context state'));
      return;
    }

    await restoreSessionContent(sessionContext, requestParams);
    store.set(startNewStateSegment, {
      allowStartNew: true,
    });
    if (requestParams.channel === CHANNELS.EMAIL.toLowerCase()) {
      restoreSelectedModules(selectedEmailModules);
      await restoreEmailModules(selectedEmailModules);
    } else if (
      requestParams.channel.toLowerCase() === CHANNELS.RTE.toLowerCase()
    ) {
      await restoreEmailModules(selectedEmailModules, true);
    }
  } catch (error) {
    Logger.error(new Error(`Error in restoreChannelModules: ${error}`));
  } finally {
    isRestoringChannelModules = false;
  }
};

export const restoreFileUploadContext = async (
  sessionId: string,
  fileUploadContext: FileUploadContext
): Promise<void> => {
  const status = fileUploadContext.fileProcessStatus.fileProcessStatus;
  const { fileId } = fileUploadContext.fileProcessStatus;
  if (status === fileProcessStatuses.FILE_PROCESS_COMPLETED) {
    const extractionDetails = fileUploadContext.fileExtractionDetails;

    // Build map and handle both array and single object cases
    const extractionDetailsMap = new Map();

    // Single extraction detail (legacy support)
    const extractionData = extractionDetails as unknown as FileUploadType;
    if (extractionData.fileId) {
      extractionDetailsMap.set(extractionData.fileId, extractionData);
    }

    store.set(extractionDetailsSegment, () => ({
      extractionDetailsMap,
    }));
  }

  // Map the status to the new FileUploadStage format
  let fileStatus:
    | 'uploading'
    | 'extracting'
    | 'analyzing'
    | 'completed'
    | 'error' = 'completed';

  if (status === fileUploadStatuses.FILE_UPLOAD_INITIATED) {
    fileStatus = 'uploading';
  } else if (
    status === fileUploadStatuses.FILE_UPLOAD_COMPLETED ||
    status === fileProcessStatuses.FILE_PROCESS_INITIATED
  ) {
    fileStatus = 'extracting';
  } else if (status === fileProcessStatuses.FILE_PROCESS_IN_PROGRESS) {
    fileStatus = 'extracting';
  } else if (status === fileProcessStatuses.FILE_PROCESS_COMPLETED) {
    fileStatus = 'completed';
  } else if (status === fileUploadStatuses.FILE_UPLOAD_ERROR) {
    fileStatus = 'error';
  } else if (status === fileProcessStatuses.FILE_PROCESS_ERROR) {
    fileStatus = 'error';
  } else if (status === fileProcessStatuses.UNSUPPORTED_FILE_LANG_ERROR) {
    fileStatus = 'error';
  }

  // Add to selectedCompletedFileIds if file is completed
  if (fileStatus === 'completed') {
    store.set(fileUploadSegment, prev => ({
      ...prev,
      selectedCompletedFileIds: [...prev.selectedCompletedFileIds, fileId],
    }));
  }
};

export const fetchUserSessionContext = async (
  payload: GetUserSessionPayload
): Promise<{ success: boolean; data?: UserSessionContext }> => {
  // Track session recovery initiation

  trackSessionRecoveryInitiate(ANALYTICS.SESSION_RECOVERY_FORM_NAME);

  store.set(contextStateSegment, prev => ({
    ...prev,
    loading: true,
    error: false,
    errorMessage: '',
    isContextValid: false,
  }));

  const result = await getUserSessionContext(payload);

  const hasErrors = Array.isArray(result?.errors) && result.errors.length > 0;

  if (result.success && result.data?.userSessionData) {
    const { requestParams, sessionContext, fileUploadContext, sessionId } =
      result.data.userSessionData;

    // Check if sessionContext is empty or if any ContentGroup has no contentVersions
    const hasInvalidContext =
      !requestParams ||
      sessionContext.length === 0 ||
      sessionContext[0].contextData.length === 0 ||
      sessionContext[0].contextData.some(contextItem => {
        // Check if this is supposed to be a ContentGroup (has contentGroupId or should have contentVersions)
        // Skip items that are clearly EmailModule containers (have selectedEmailModules)
        if ('selectedEmailModules' in contextItem) {
          return false; // This is not a ContentGroup, skip validation
        }

        // For ContentGroup items, check if contentVersions exists and has content
        return (
          !contextItem.contentVersions ||
          contextItem.contentVersions.length === 0
        );
      });

    if (hasInvalidContext) {
      store.set(contextStateSegment, prev => ({
        ...prev,
        loading: false,
        error: true,
        errorMessage: 'No session context found',
        isContextValid: false,
        requestPayload: null,
      }));
      return { success: false };
    }

    const { contextData, contentSessionId } = sessionContext[0];
    const updatedRequestParams = requestParams
      ? { ...requestParams }
      : requestParams;
    setSessionId(sessionId);
    setContentSessionId(contentSessionId);
    if (
      sessionContext[0]?.pregenerateData &&
      sessionContext[0]?.pregenerateData?.identifiedClaims
    ) {
      if (
        sessionContext[0]?.pregenerateData?.selectedPrimaryMessages &&
        sessionContext[0]?.pregenerateData?.selectedPrimaryMessages?.length &&
        requestParams
      ) {
        updatedRequestParams.topics =
          sessionContext[0].pregenerateData.selectedPrimaryMessages;
      }
      store.set(preGenerateCliamsStateSegment, prev => ({
        ...prev,
        preFetchedclaimsList:
          sessionContext[0].pregenerateData.identifiedClaims || [],
        isPrefetchedClaimsLoading: false,
        isPrefetchedClaimsError: false,
        isPrefetchedClaimsSuccess: true,
        quotes: Array.isArray(
          sessionContext[0].pregenerateData.identifiedQuotes
        )
          ? sessionContext[0].pregenerateData.identifiedQuotes
          : prev.quotes,
        extendedLibrary:
          typeof sessionContext[0].pregenerateData.extendedLibrary === 'boolean'
            ? sessionContext[0].pregenerateData.extendedLibrary
            : prev.extendedLibrary,
      }));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let documentInfo: any = null;
    if (fileUploadContext.length) {
      documentInfo = fileUploadContext?.[0]?.documentInfo || {};
      await restoreFileUploadContext(payload.sessionId, fileUploadContext[0]);
    }

    const selectedEmailModules = contextData.flatMap(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (ctx: any) => ctx.selectedEmailModules || []
    );

    store.set(contextStateSegment, prev => ({
      ...prev,
      requestPayload: updatedRequestParams,
      selectedEmailModules,
      sessionContext,
      documentInfo,
      error: hasErrors,
      errorMessage: hasErrors ? result.errors![0] : '',
      loading: false,
      isContextValid: !hasErrors,
    }));
    store.set(sessionRestoreModalSegment, { isVisible: !hasErrors });
    // Track session recovery completion
    trackSessionRecoveryComplete(ANALYTICS.SESSION_RECOVERY_FORM_NAME);

    return { success: true, data: result.data.userSessionData };
  }

  const fallbackError =
    result?.errors && result.errors.length > 0
      ? result.errors[0]
      : 'Unknown error occurred';

  // Track session recovery error
  trackSessionRecoveryError(ANALYTICS.SESSION_RECOVERY_FORM_NAME);

  store.set(contextStateSegment, prev => ({
    ...prev,
    loading: false,
    error: true,
    errorMessage: fallbackError,
    isContextValid: false,
    requestPayload: null,
  }));

  return { success: false };
};
